define('home/models/Thread',[
    'jquery',
    'underscore',
    'backbone',
    'dompurify',

    'core/api',
    'core/bus',
    'core/models/mixins',
    'core/models/Thread',
    'core/UniqueModel',
    'core/utils',
    'core/utils/auth',
    'core/utils/html',

    'home/collections/common/ExtensibleCollection',
    'home/collections/TopicCollection',
    'home/models/Forum',
    'home/models/User',
    'home/models/Post',
    'home/templates/confirmDeleteThread',
    'home/utils/backboneUtils',
    'home/utils/datetimeUtils',
    'home/utils/confirmModal',
    'home/utils/navigationUtils',
], function (
    $,
    _,
    Backbone,
    dompurify,

    api,
    bus,
    mixins,
    BaseThread,
    UniqueModel,
    utils,
    auth,
    html,

    ExtensibleCollection,
    TopicCollection,
    Forum,
    User,
    Post,
    confirmDeleteThreadTemplate,
    backboneUtils,
    datetimeUtils,
    confirmModal,
    navigationUtils
) {
    'use strict';

    var SHARER_ENCODING_BASE = 36;

    // An "original thread" is a thread created on Home.
    // For original threads our backend returns a thread.link value
    // of the form: https://<forum_shortname>.disqus.com/thread/<slug>
    var originalThreadUrlRegex = /^https:\/\/[^.]+\.disqus\.com\/thread\//;

    var parent = BaseThread;
    var parentProto = parent.prototype;


    var Thread = parent.extend({
        defaults: _.defaults({
            id: null,

            content: { // populated by Embedly
                title: '',
                description: '',
                keywords: [],
                image: {
                    url: '',
                    height: '',
                    width: '',
                },
            },

            // Used when starting a discussion to supplement the
            // discussion/thread title
            message: undefined, // e.g., a caption or a link

            // Whether or not the currently authenticated user can moderate/
            // can edit/is the author of this thread.
            // These are computed and may change to true after initialization.
            canModerate: false,
            canEdit: false,
            isAuthor: false,
        }, parentProto.defaults),

        initialize: function (attrs, options) {
            parentProto.initialize.call(this, attrs, options);

            this.initializeRelated('forum', options, Forum, 'name');

            this.listenTo(this, 'change:forum', function (_thread, value) {
                if (!(value instanceof Backbone.Model))
                    this.forum.set(value);
            });

            this.listenTo(this, 'change:author', function (_thread, author) {
                this.setAuthor(author);
            });

            this.listenTo(this, 'change:message', this._clearDescriptions);

            this.topics = new TopicCollection([], { thread: this });
            var self = this;
            backboneUtils.getPromiseFor(this, 'topics').then(function (topics) {
                self.topics.add(topics);

                // Unset attribute once values have been copied to the Collection
                self.unset('topics');
            });

            this.checkUserGenerated(options);

            this.listenTo(this, 'change:userSubscription', this.onChangeUserSubscription);
        },

        onChangeUserSubscription: function (_model, isSubscribed) {
            // Propagate subscription change event to session user
            var Session = require('home/models/Session');
            var session = Session.get();
            session.loadPromise().always(_.bind(function () {
                session.user.trigger(isSubscribed ? 'subscribe:thread' : 'unsubscribe:thread', this);
            }, this));
        },

        validate: function (attrs) {
            if (!attrs.title || !attrs.title.trim()) {
                return {
                    attrName: 'title',
                    message: 'Please enter a title for your discussion.',
                };
            }

            if (attrs.title.length > 200) {
                return {
                    attrName: 'title',
                    message: 'Please shorten your title.',
                };
            }

            if (!attrs.message || attrs.message.trim().length < 2) {
                return {
                    attrName: 'message',
                    message: 'Please enter a longer discussion body',
                };
            }

            var channelCategories = this.forum.channel && this.forum.channel.getCategories();
            if (channelCategories) {
                var chosenCategory = _.chain(channelCategories).keys().find(function (category) {
                    return attrs.title.indexOf(category) === 0;
                }).value();

                // Leave the validation of categories to topic collection's validation.
                // Just validate title here.

                if (chosenCategory && !attrs.title.match(new RegExp(chosenCategory + '.*\\w+'))) {
                    return {
                        attrName: 'title',
                        message: 'Your title must contain more than just a category.',
                    };
                }
            }
        },

        checkUserGenerated: function (options) {
            options = options || {};

            // Cannot initialize this as a User Generated Thread until the details have
            // been fetched
            if (this.shouldFetch()) {
                this.listenToOnce(this, 'sync', _.bind(this.checkUserGenerated, this, options));
                return;
            }

            if (!this.forum.channel) {
                this.set({
                    isUserGenerated: false,
                });

                // No futher work required for non-User Generated Threads.
                return;
            }

            this.setAuthor(options.author);
            this.initializeRecentPosts(options);

            this.set({
                isUserGenerated: true,
            });
        },

        setAuthor: function (author) {
            author = author || this.get('author');

            if (this.author) {
                if (_.isObject(author))
                    this.author.set(author);
            } else if (_.isObject(author)) {
                this.initializeRelated('author', { author: author }, User);
            } else {
                author = this.getAuthorFromUniqueModel(author);
                if (author)
                    this.author = author;
            }
        },

        getAuthorFromUniqueModel: function (author) {
            if (!_.isString(author) || !author.length)
                return;

            return _.find(UniqueModel.pool(User), function (user) {
                return user.get('id') === author;
            });
        },

        fetchAuthor: function () {
            var author = this.get('author');
            var model;

            model = this.getAuthorFromUniqueModel(author);
            if (model) {
                this.setAuthor(model);
                return $.Deferred().resolve(model).promise();
            }

            model = new User({ id: author });
            return model.fetch().then(_.bind(function () {
                this.setAuthor(model.attributes);
            }, this));
        },

        initializeRecentPosts: function (options) {
            if (!Post)
                Post = require('home/models/Post');

            this.recentPosts = new Backbone.Collection(
                _.map(options.recentPosts,
                    function (post) {
                        if (post instanceof Post)
                            return post;

                        return new UniqueModel(Post,
                            post,
                            { thread: this }
                        );
                    },
                    this
                ));
        },

        getLinks: function () {
            var links = [];

            if (this.get('isUserGenerated'))
                links = this.getLinksFromMessage();
            else
                links = [this.get('link')];

            return links;
        },

        getFriendlySourceUrl: function () {
            var friendlyUrl = utils.getDomain(this.get('link'));
            return friendlyUrl.indexOf('disqus.com') > -1 ? 'disqus.com' : friendlyUrl;
        },

        getLinksFromMessage: _.memoize(function () {
            var $wrapper = $('<div>').html(this.get('message'));
            return $wrapper.find('a[href]').map(function (__, el) {
                return $(el).attr('href');
            });
        }, function () {
            return this.id;
        }),

        shouldFetch: function () {
            return !this.get('clean_title');
        },

        url: function (type) {
            return this.urls[type] || this.urls.read;
        },

        urls: {
            'read': api.getURL('threads/details'),
            'create': api.getURL('threads/create'),
            'update': api.getURL('threads/update'),
            'delete': api.getURL('threads/remove'),
            'restore': api.getURL('threads/restore'),
            'inviteUserGroup': api.getURL('threads/inviteUserGroup'),
            'approve': api.getURL('threads/approve'),
            'spam': api.getURL('threads/spam'),
        },

        buildFetchData: function (existing) {
            var data;

            if (this.get('id')) {
                data = {
                    thread: this.get('id'),
                };
            } else if (this.get('slug') && this.forum.id) {
                data = {
                    thread: 'slug:' + this.get('slug'),
                    forum: this.forum.id,
                };
            } else {
                throw new Error('Cannot fetch thread without an id or a slug plus forum');
            }

            return _.extend(data, existing);
        },

        fetch: function (options) {
            if (this.fetchPromise)
                return this.fetchPromise;

            options = options || {};
            options.data = _.extend(this.buildFetchData(), options.data);

            // Topics must be requested explicitly for threads
            options.data.attach = options.data.attach || [];
            options.data.attach.push('topics');

            if (this.forum.shouldFetch()) {
                options.data.related = options.data.related || [];
                options.data.related.push('forum');
            }

            this.fetchPromise = Backbone.Model.prototype.fetch.call(this, options);

            var self = this;
            this.fetchPromise.always(function () {
                self.fetchPromise = null;
            });

            return this.fetchPromise;
        },

        sync: function (method, self, options) {
            var attrs = self.attributes;
            options = options || {};

            options.url = this.url(method);

            // Must set processData to true, otherwise Backbone.sync sets it to false,
            // which results in a POST body of '[object Object]' to the API endpoint
            options.processData = true;

            // Must set emulateHTTP to true so that the request type will be POST instead
            // of PUT/DELETE/PATCH
            options.emulateHTTP = true;

            switch (method) {
            case 'create':
                var forum = attrs.forum;
                if (!attrs.forum)
                    forum = this.forum.id;


                options.data = _.extend({
                    title: attrs.title,
                    forum: forum,
                    message: attrs.message,
                }, options.data);

                break;
            case 'update':
                // Update the only fields that are updatable through home
                var updatable = {
                    title: attrs.title,
                    message: attrs.message,
                };

                options.data = _.extend({ thread: attrs.id }, updatable);
                break;
            case 'delete':
                options.data = _.extend({ thread: attrs.id }, options.data);
                break;
            case 'restore':
            case 'inviteUserGroup':
            case 'spam':
            case 'approve':
                // TODO: we should only use sync for crud operations, so this case should be moved to it's own RPC type method in the future.
                options.data = _.extend({ thread: attrs.id }, options.data);
                options.type = 'POST';
                break;
            }

            // TODO - call base class sync method
            return Backbone.sync(method, self, options);
        },

        parse: function (resp) {
            // resp differs depending on whether model.parse
            // is called as a result of model.fetch or collection.fetch
            return resp.response || resp;
        },

        toJSON: function () {
            var json = parentProto.toJSON.call(this);

            _.extend(json, {
                forum: this.forum.toJSON(),
                author: this.author ? this.author.toJSON() : undefined,
            });

            if (!json.link) {
                // try to make link go to the Discussion page on Home
                if (json.slug)
                    json.link = this.getDiscussionRoute();
            } else if (originalThreadUrlRegex.test(json.link)) {
                json.link = this.getDiscussionRoute();
            }

            return json;
        },

        getThread: function () {
            return this;
        },

        getHostname: function () {
            var url = this.get('link');
            if (!url)
                return '';

            var anchor = $('<a>').attr('href', url)[0];

            return anchor.hostname || url;
        },

        // Deprecated
        getRelativeCreatedAt: function () {
            return datetimeUtils.getRelativeDatetime(this.get('createdAt'));
        },

        // Deprecated
        getRelativeCommentTimestamp: function () {
            return datetimeUtils.getRelativeDatetime(this.get('most_recent_comment_timestamp'));
        },

        // Deprecated
        // Returns the user details of the most recent commenter, if
        // the most recent commenter exists and is not anonymous
        getMostRecentCommenter: function () {
            var commenter = this.get('most_recent_commenter');
            if (commenter && !commenter.isAnonymous)
                return commenter;
        },

        // Extends this model with embedly data
        setEmbedlyData: function (data) {
            this.set({
                content: {
                    title: dompurify.sanitize(data.title),
                    description: dompurify.sanitize(data.description),
                    image: data.images && data.images[0] || {},
                    keywords: data.keywords.length ?
                        data.keywords.map(function (kword) { return dompurify.sanitize(kword); }).slice(0, 3) :
                        [],
                },
            });
        },

        getImage: function () {
            var image;
            if (this.get('isUserGenerated'))
                image = this.getUserGeneratedImage();

            return image || this.getEmbedlyImage();
        },

        getEmbedlyImage: function () {
            var content = this.get('content');
            if (content && content.image && content.image.url && this.hasValidImageUrl(content.image)) {
                return {
                    src: content.image.url,
                    title: this.get('title'),
                };
            }
        },

        hasValidImageUrl: function (image) {
            // Check that url param from returned cdn image link isn't empty
            var matches = image.url.match(/url=([^&]+)/m);
            return matches && matches.length > 1 && matches[1].length;
        },

        getUserGeneratedImage: function () {
            var media = this.getMedia();
            var firstMedia = media && media[0];

            if (firstMedia && firstMedia.thumbnailUrl) {
                return {
                    src: firstMedia.thumbnailUrl,
                    title: firstMedia.title,
                };
            }
        },

        getMedia: function () {
            return this.get('media');
        },

        hasPreview: function () {
            var content = this.get('content');
            return Boolean(content && (content.image.url || content.description));
        },

        // TODO - rename to use 'vote' terminology like base class
        toggleFavorited: function () {
            var vote = this.get('userScore') ? 0 : 1;

            if (vote === 1)
                bus.trigger('threadFavorited', this);

            parentProto.vote.call(this, vote);
        },

        toggleIsSpam: function () {
            this.markAsSpam(!this.get('isSpam'));
        },

        /**
         * Marks the thread as either spam or not spam, as specified by
         * the given argument. If nothing is passed, will mark as spam.
         * @param  {boolean} [isSpam] - Whether or not this thread is spam.
         *                              Defaults to true.
         */
        markAsSpam: function (isSpam) {
            // Default to true
            isSpam = isSpam !== false;

            var onSuccess = _.bind(this.set, this, 'isSpam', isSpam);
            this.sync(isSpam ? 'spam' : 'approve', this).then(onSuccess);
        },

        /**
          * Returns the embedly excerpt of the default embedly length
          * of 70 words.
          * @returns {string} - description shortened to 70 words
          */
        getDefaultDescription: function () {
            return this._getDescription(70);
        },

        /**
         * Returns the embedly excerpt of the short embedly length
         * of 50 words.
         * @returns {string} - description shortened to 70 words
         */
        getShortDescription: function () {
            return this._getDescription(50);
        },

        /**
          * Returns a longer embedly excerpt than default. Used in
          * views such as the discussion cover view.
          * @returns {string} - description shortened to 200 words
          */
        getLongDescription: function () {
            return this._getDescription(200);
        },

        getFullDescription: function () {
            return this._getDescription(Infinity);
        },

        /**
         * Returns a plain text description without any HTML,
         * URLs or newlines
         * @returns {string} - description with tags removed
         */
        getPlainDescription: function () {
            // Important to call strip tags before truncating.
            // Use getFullDescription to get the proper description field
            // and gaurantee a string is returned (possibly empty)
            return this._truncate(html.stripTags(this.getFullDescription()), 50);
        },

        /**
          * Returns the thread description truncated to roughly numWords.
          * @param {number} numWords - target number of words
          * @returns {string}        - thread description truncated
          */
        _getDescription: function (numWords) {
            // Try to use an earlier truncated description for the given numWords
            if (this.descriptions && this.descriptions[numWords])
                return this.descriptions[numWords];

            var description;

            if (this.get('isUserGenerated'))
                description = this.get('message');
            else if (this.has('content'))
                description = this.get('content').description;

            if (!description || !numWords)
                return '';

            var truncated = this._truncate(description, numWords);
            if (this.get('shouldSanitizeMessage'))
                truncated = _.escape(truncated);
            this._storeDescription(truncated, numWords);
            return truncated;
        },

        /**
          * Returns the given text truncated to roughly numWords.
          * This returns a number of words as close to the given number
          * of words as possible while maintaining full sentences.
          * @param {string} text     - given text
          * @param {number} numWords - target number of words
          * @returns {string}        - given text truncated
          */
        _truncate: function (text, numWords) {
            var words = text.split(' ');
            var wordsLength = words.length;

            // Not enough words to return. Return as much as possible.
            if (numWords >= wordsLength)
                return text;

            var distance,
                breakIndex,
                word;

            // Start looking for a break point, starting at the exact number of words
            // requested and moving outwards, looking first forward and then back at
            // each further distance. (Prefer more words to less words).
            for (distance = 0; numWords - 1 - distance >= 0 || numWords + distance - 1 < wordsLength; distance++) {
                // Consider word <distance> ahead of requested numWords
                breakIndex = numWords - 1 + distance;
                word = words[breakIndex];
                if (word && word.slice(-1) === '.')
                    break;

                // Consider word <distance> prior to requested numWords
                breakIndex = numWords - 1 - distance;
                word = words[breakIndex];
                if (word && word.slice(-1) === '.')
                    break;

                // In case the search exits without having seen a single break point,
                // will want to return the entire text.
                breakIndex = wordsLength - 1;
            }

            // Return truncated text
            return words.slice(0, breakIndex + 1).join(' ');
        },

        _storeDescription: function (description, numWords) {
            if (!this.descriptions)
                this.descriptions = {};

            this.descriptions[numWords] = description;
        },

        // Clear cached descriptions.
        _clearDescriptions: function () {
            this.descriptions = {};
        },

        // TODO - get this out of the model, models should have no knowledge of things like modals
        confirmDelete: function (preventNavigation) {
            confirmModal.confirmationPromise(confirmModal.TYPES.CONFIRM_CANCEL,
                confirmDeleteThreadTemplate(this.toJSON()))
                .then(_.bind(function (isConfirmed) {
                    if (!isConfirmed)
                        return;

                    this.removeThread();

                    if (!preventNavigation) {
                        navigationUtils.navigate('/home/channel/' + this.forum.channel.id + '/', {
                            trigger: true,
                            replace: true,
                        });
                    }
                }, this));
        },

        removeThread: function () {
            if (this.topics.length > 0) {
                // Make a new, empty topic collection so it can compare
                // this differences properly when syncing
                var newTopicsCollection = new TopicCollection([], { thread: this });
                newTopicsCollection.save();
            }

            if (!this.get('isDeleted'))
                this.sync('delete', this).then(_.bind(this.set, this, { isDeleted: true }));

            if (!this.get('isClosed'))
                this.sync('close', this).then(_.bind(this.set, this, { isClosed: true }));
        },

        // Un-deletes a thread
        restore: function () {
            if (this.get('isDeleted') || this.get('isClosed')) {
                this.sync('restore', this).then(_.bind(this.set, this, { isDeleted: false }));
                this.sync('open', this).then(_.bind(this.set, this, { isClosed: false }));
            }
        },

        toggleSubscription: function () {
            return this.subscribe(!this.get('userSubscription'));
        },

        /**
         * Invites user group by name.
         * @param {string} groupName - user's group name
         * @returns {Promise}        - sync request (ajax implemented through Backbone)
         */
        inviteUserGroup: function (groupName) {
            return this.sync('inviteUserGroup', this, { data: { userGroup: groupName } });
        },

        /**
         * Returns a promise which will resolve with a boolean representing
         * whether or not the given session can make updates to this thread.
         *
         * Thread authors, thread moderators, and Disqus admins can edit non
         * deleted threads.
         *
         * @param {Model} session - The Session Model
         * @returns {Promise}     - A promise that will resolve with a boolean
         */
        getEditPermissions: function (session) {
            if (this.get('isDeleted'))
                return $.Deferred().resolve(false).promise();

            return $.when(this.isAuthorSessionUser(session), this.getModeratePermissions(session)).then(
                _.bind(function (isAuthorSessionUser, canModerate) {
                    var canEdit = isAuthorSessionUser || canModerate;
                    this.set('canEdit', canEdit);
                    return canEdit;
                }, this)
            );
        },

        /**
         * Returns a promise which will resolve with a boolean representing
         * whether or not the session user can moderate this Thread.
         *
         * @param  {Model} session - The Session Model
         * @returns {Promise} - A promise that will resolve with a boolean
         */
        getModeratePermissions: function (session) {
            if (this.get('canModerate') || auth.getFromCookie().staff) {
                this.set('canModerate', true);
                return $.Deferred().resolve(true).promise();
            }

            return session.loadModeratedForums().then(_.bind(function () {
                var canModerate = _.contains(session.moderatedForums, this.forum.id);
                this.set('canModerate', canModerate);
                return canModerate;
            }, this));
        },

        /**
         * Returns a promise which will resolve with a boolean representing
         * whether or not the current session user is the author of this Thread.
         *
         * @param {Model} session - The Session Model
         * @returns {Promise} - A promise that will resolve with a boolean
         */
        isAuthorSessionUser: function (session) {
            // Thread may have converted author attribute into user model, which
            // may not yet be fully fetched. Compare ids instead of whole models.
            var authorId = this.author ?
                this.author.get('id') :
                this.get('author');

            return session.loadPromise().then(_.bind(function () {
                var isAuthor = session.user.get('id') && session.user.get('id') === authorId;
                this.set('isAuthor', isAuthor);
                return isAuthor;
            }, this));
        },

        getShareUrl: function () {
            return 'https://disq.us/t/' + Number(this.id).toString(SHARER_ENCODING_BASE);
        },

        shareThread: function (network) {
            var urlToShare = this.getShareUrl();
            var shareTitle = this.get('clean_title');
            var baseUrl, shareUrl;

            switch (network) {
            case 'twitter':
                baseUrl = 'https://twitter.com/intent/tweet';
                shareUrl = baseUrl +
                    '?text=' + window.encodeURIComponent(shareTitle) +
                    '&url=' + window.encodeURIComponent(urlToShare);
                break;
            case 'facebook':
                baseUrl = 'https://www.facebook.com/sharer.php';
                shareUrl = baseUrl + '?u=' + window.encodeURIComponent(urlToShare);
                break;
            default:
                return;
            }

            this._showSharePopup(shareUrl);
        },

        _showSharePopup: function (url) {
            return utils.openWindow(url, '_blank', { width: 550, height: 520 });
        },

        storageKey: function () {
            if (!this.isNew())
                return;

            if (!this.forum.id)
                return;

            return ['drafts', 'forum', this.forum.id].join(':');
        },

    }, {
        // The string separating the user's title from the category, if any
        CATEGORY_TITLE_SEPARATOR: ': ',

        getOrCreateThread: function (forumId, threadSlug, fetchOptions) {
            var threads = UniqueModel.pool(Thread);
            var thread = _.find(threads, function (thread) {
                return thread.get('slug') === threadSlug && thread.forum.get('id') === forumId;
            });

            if (!thread) {
                // Necessary to insert thread into an ExtensibleCollection so that the fetch for
                // embedly data will kick off after the thread fetch completes.
                var directThreads = new ExtensibleCollection();

                thread = new Thread({
                    slug: threadSlug,
                    forum: {
                        id: forumId,
                    },
                });

                directThreads.add(thread);
                thread.fetch(fetchOptions).then(function () {
                    // Can't create a unique model initially since we only have the thread slug,
                    // not the id. Once the id is fetched, register thread with unique model.
                    UniqueModel.set(Thread, thread);
                });
            }

            return thread;
        },
    });

    UniqueModel.addType('Thread', Thread);
    mixins.withCreatedAt.call(Thread.prototype);

    return Thread;
});

